import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { Fir5471ScheduleJService } from './fir-5471-schedule-j.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

interface ColumnHeader {
  row_span_value?: number;
  col_span_value?: number;
  header_value: string;
}

interface ColumnDescription {
  VALUE: number | null;
  line_attrib_key: string;
  attrib_name: string;
  trans_details_key: string;
  occurrence: string;
  is_editable: boolean;
  is_Display: boolean;
  column_style?: any;
  attrib_order: number;
}

interface ColTotalLineItem {
  line_no: string;
  line_no_for_total: { line_no: string; multiplier: number }[];
  copy_total_line_no?: string;
}

interface GHOSTLineItem {
  line_no: string;
  col_data: {
    ADJ_AMT: any;
    LINE_ATTRIB_KEY: string;
    ATTRIB_NAME: string;
    FORM_KEY: string;
    OVERRIDE_Y_N: string | null;
    TRANS_DETAILS_KEY: string | null;
    TRANS_HEADER_KEY: string | null;
    TRANS_TYPE_KEY: string | null;
  }[];
}

interface ScheduleJLineItem {
  line_no: string;
  part_no?: string;
  line_Description: string;
  column_description: ColumnDescription[];
  saveParams: {
    tax_year: string;
    trans_type_key: string;
    trans_Details_key: string;
    occurrence: string;
    form_key: string;
  };
}

@Component({
    selector: 'gtw-fir-5471-schedule-j',
    templateUrl: './fir-5471-schedule-j.component.html',
    styleUrls: ['./fir-5471-schedule-j.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Fir5471ScheduleJComponent implements OnInit, OnDestroy {
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private unsubscribe$ = new Subject<void>();
  private subscriptions = new Subscription();

  modalTitle = 'Schedule J: Accumulated Earnings and Profits (E&P) of Controlled Foreign Corporation';
  form_header: string | null = null;
  table_header: string | null = null;
  dataLoading = false;
  crudLoading = false;
  userMessage = '';
  minimise = false;
  isInitialized = false;
  loadFlag = false;
  saveFlag = true;

  irsF5471ScheduleJData: ScheduleJLineItem[] = [];
  irsF5471ScheduleJOriginalData: ScheduleJLineItem[] = [];
  columnHeader: ColumnHeader[] = [];
  columnSubHeader: string[] = [];
  partHeader: string[] = [];
  GHOSTAmt: GHOSTLineItem[] = [];
  col_total_line_arr: ColTotalLineItem[] = [];
  partFlag = false;

  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};
  taxYear = 2018;

  constructor(
    private fir5471ScheduleJService: Fir5471ScheduleJService,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.resetAndTryInit();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydratedGp = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydratedGp);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydratedGp) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    const rowObj = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.rowData = rowObj;
    this.taxYear = parseInt(
      String(this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year || '2018'),
      10
    );
    this.initColTotalLineArr();
  }

  private resetAndTryInit(): void {
    this.isInitialized = false;
    this.tryInit();
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    const baseUrl = this.baseURLs?.api || this.baseURLs?.base_url || this.GENERAL_CONFIG?.base_url;
    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    if (!baseUrl || !combinationKey || !this.globalService?.globalParams) {
      return;
    }
    this.isInitialized = true;
    this.loadScheduleJData();
  }

  initColTotalLineArr(): void {
    if (this.taxYear < 2018) {
      this.partFlag = false;
      this.col_total_line_arr = [
        {
          line_no: '3',
          line_no_for_total: [
            { line_no: '1', multiplier: 1 },
            { line_no: '2A', multiplier: 1 },
            { line_no: '2B', multiplier: -1 }
          ]
        },
        {
          line_no: '6A',
          line_no_for_total: [
            { line_no: '1', multiplier: 1 },
            { line_no: '4', multiplier: 1 },
            { line_no: '5A', multiplier: -1 }
          ],
          copy_total_line_no: '7'
        },
        {
          line_no: '6B',
          line_no_for_total: [
            { line_no: '3', multiplier: 1 },
            { line_no: '4', multiplier: -1 },
            { line_no: '5B', multiplier: -1 }
          ],
          copy_total_line_no: '7'
        }
      ];
    } else {
      this.partFlag = true;
      this.col_total_line_arr = [
        {
          line_no: 'I-1C',
          line_no_for_total: [
            { line_no: 'I-1A', multiplier: 1 },
            { line_no: 'I-1B', multiplier: 1 }
          ]
        },
        {
          line_no: 'I-7',
          line_no_for_total: [
            { line_no: 'I-1C', multiplier: 1 },
            { line_no: 'I-2A', multiplier: 1 },
            { line_no: 'I-2B', multiplier: 1 },
            { line_no: 'I-3', multiplier: 1 },
            { line_no: 'I-4', multiplier: 1 },
            { line_no: 'I-5A', multiplier: 1 },
            { line_no: 'I-5B', multiplier: 1 },
            { line_no: 'I-6', multiplier: 1 }
          ]
        },
        {
          line_no: 'I-14',
          line_no_for_total: [
            { line_no: 'I-7', multiplier: 1 },
            { line_no: 'I-8', multiplier: 1 },
            { line_no: 'I-9', multiplier: 1 },
            { line_no: 'I-10', multiplier: 1 },
            { line_no: 'I-11', multiplier: 1 },
            { line_no: 'I-12', multiplier: 1 },
            { line_no: 'I-13', multiplier: 1 }
          ]
        }
      ];
    }
  }

  loadScheduleJData(): void {
    this.unsubscribe$.next();
    this.irsF5471ScheduleJData = [];
    this.irsF5471ScheduleJOriginalData = [];
    this.columnHeader = [];
    this.columnSubHeader = [];
    this.form_header = null;
    this.table_header = null;
    this.GHOSTAmt = [];
    this.partHeader = [];

    const combinationKey = this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY;
    const params = {
      action_code: 'dlxcms',
      irs_form_no: 'F5471',
      combination_key: combinationKey,
      jcd_key: 250
    };

    const url = (this.baseURLs?.api || this.baseURLs?.base_url || this.GENERAL_CONFIG?.base_url) + '/loadJsonObject';
    this.dataLoading = true;

    this.jsonObjectFactory
      .getJSONObj(url, params, this.globalService, null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (data.callSuccess === '1') {
          this.dataLoading = false;
          const irsF5471ScheduleJDataTemp = data.jsonObject;
          this.processScheduleJData(irsF5471ScheduleJDataTemp);
          if (this.taxYear < 2018) {
            this.setAdjAmt();
          } else {
            this.setGHOSTAmt();
          }
          this.cdr.markForCheck();
        } else {
          this.dataLoading = false;
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar(
              'Sorry you do not have access to do the requested action.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          } else {
            this.gtwSnackbarService.openSnackBar(
              'An error has occurred, If you continue to experience this issue please visit the help page to contact support.',
              'gtw-snackbar--red'
            );
            this.userMessage = '!! Unable to perform Selected Action...';
          }
          this.cdr.markForCheck();
        }
      });
  }

  processScheduleJData(irsF5471ScheduleJDataTemp: any[]): void {
    let i = 1;
    if (!irsF5471ScheduleJDataTemp || irsF5471ScheduleJDataTemp.length <= 1) return;
    let previous_line_no = irsF5471ScheduleJDataTemp[i].line_no;

    while (i < irsF5471ScheduleJDataTemp.length) {
      const tempObj: any = {};
      let j = i;
      let index = 0;
      tempObj.column_description = [];

      while (
        j < irsF5471ScheduleJDataTemp.length &&
        previous_line_no === irsF5471ScheduleJDataTemp[j].line_no
      ) {
        const objIdParts = irsF5471ScheduleJDataTemp[j].object_id?.split('~') || [];
        const typeId = objIdParts[5];

        if (typeId === 'FH') {
          this.form_header = irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || null;
        } else if (typeId === 'TH') {
          this.table_header = irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || null;
        } else if (typeId === 'CH') {
          const attr0 = irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[0];
          if (attr0 !== 'PREVIOUSLY_TAXED_E_AND_P_HEADER') {
            this.columnHeader.push({
              row_span_value: 2,
              header_value: irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || ''
            });
          } else {
            this.columnHeader.push({
              col_span_value: this.taxYear < 2018 ? 3 : 9,
              header_value: irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || ''
            });
          }
        } else if (typeId === 'PH') {
          this.partHeader.push(irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || '');
        } else if (typeId === 'CSH') {
          this.columnSubHeader.push(irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || '');
        } else {
          const attrib0 = irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[0];
          if (attrib0 === 'LINE_NO') {
            tempObj.line_no = irsF5471ScheduleJDataTemp[j].line_no;
            if (irsF5471ScheduleJDataTemp[j].line_no?.split('-').length > 1) {
              tempObj.part_no = irsF5471ScheduleJDataTemp[j].line_no.split('-')[0];
            }
            tempObj.saveParams = {
              tax_year: objIdParts[0] || '',
              trans_type_key: objIdParts[1] || '',
              trans_Details_key: objIdParts[3] || '',
              occurrence: objIdParts[4] || '',
              form_key: objIdParts[6] || ''
            };
          } else if (attrib0 === 'LINE_DESCRIPTION') {
            tempObj.line_Description = irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[3] || '';
          } else {
            const propType = irsF5471ScheduleJDataTemp[j].PROPERTY_TYPE;
            const style = { width: '150px' };
            if (propType === '9' || propType === '8') {
              tempObj.column_description[index] = {
                VALUE: irsF5471ScheduleJDataTemp[j].VALUE !== undefined ? irsF5471ScheduleJDataTemp[j].VALUE : null,
                line_attrib_key: irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[1] || '',
                attrib_name: irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[0] || '',
                trans_details_key: objIdParts[3] || '',
                occurrence: objIdParts[4] || '',
                is_editable: propType === '9',
                is_Display: true,
                column_style: style,
                attrib_order: parseInt(irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[4] || '0', 10)
              };
            } else if (propType === '0') {
              tempObj.column_description[index] = {
                VALUE: null,
                line_attrib_key: irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[1] || '',
                attrib_name: irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[0] || '',
                trans_details_key: objIdParts[3] || '',
                occurrence: objIdParts[4] || '',
                is_editable: false,
                is_Display: false,
                column_style: { 'background-color': '#D6D6D6', width: '150px' },
                attrib_order: parseInt(irsF5471ScheduleJDataTemp[j].attrib_value?.split('~')[4] || '0', 10)
              };
            }
            index++;
          }
        }
        previous_line_no = irsF5471ScheduleJDataTemp[j].line_no;
        j++;
      }

      if (j < irsF5471ScheduleJDataTemp.length) {
        previous_line_no = irsF5471ScheduleJDataTemp[j].line_no;
      }

      if (tempObj.line_no !== undefined) {
        tempObj.column_description = _.sortBy(tempObj.column_description, 'attrib_order');
        this.irsF5471ScheduleJData.push(tempObj);
      }
      i = j;
    }
  }

  setAdjAmt(): void {
    const params = {
      action_code: 'pg37o0',
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      combination_key: this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY,
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: 250
    };

    const url = (this.baseURLs?.api || this.baseURLs?.base_url || this.GENERAL_CONFIG?.base_url) + '/loadJsonObject';
    this.jsonObjectFactory
      .getJSONObj(url, params, this.globalService, null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (data.callSuccess === '1') {
          const temp_adj_amt = data.jsonObject?.[0];
          if (temp_adj_amt) {
            const line_obj = _.find(this.irsF5471ScheduleJData, { line_no: temp_adj_amt.LINE_NO });
            const col_obj = line_obj ? _.find(line_obj.column_description, { attrib_name: 'UNDISTRIBUTED_EARNINGS' }) : null;
            if (col_obj) {
              col_obj.VALUE = temp_adj_amt.ADJ_AMT_1 < 0 ? temp_adj_amt.ADJ_AMT_1 * -1 : temp_adj_amt.ADJ_AMT_1;
            }
          } else {
            const line2A = _.find(this.irsF5471ScheduleJData, { line_no: '2A' });
            const line2B = _.find(this.irsF5471ScheduleJData, { line_no: '2B' });
            const col2A = line2A ? _.find(line2A.column_description, { attrib_name: 'UNDISTRIBUTED_EARNINGS' }) : null;
            const col2B = line2B ? _.find(line2B.column_description, { attrib_name: 'UNDISTRIBUTED_EARNINGS' }) : null;
            if (col2A) col2A.VALUE = 0;
            if (col2B) col2B.VALUE = 0;
          }
          this.setGHOSTAmt();
          this.cdr.markForCheck();
        } else {
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('An error has occurred, If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        }
      });
  }

  setGHOSTAmt(): void {
    const params = {
      action_code: 'k6iobc',
      tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
      combination_key: this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY,
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key || 250
    };

    const url = (this.baseURLs?.api || this.baseURLs?.base_url || this.GENERAL_CONFIG?.base_url) + '/loadJsonObject';
    this.jsonObjectFactory
      .getJSONObj(url, params, this.globalService, null, null)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: any) => {
        if (data.callSuccess === '1') {
          const responseData = data.jsonObject || [];
          this.GHOSTAmt = [];

          if (responseData.length > 0) {
            let i = 0;
            while (i < responseData.length) {
              let j = i;
              const line_no = responseData[i].LINE_NO;
              const tempObj: GHOSTLineItem = {
                line_no,
                col_data: []
              };
              let k = 0;
              while (j < responseData.length && line_no === responseData[j].LINE_NO) {
                tempObj.col_data[k] = {
                  ADJ_AMT: responseData[j].ADJ_AMT,
                  LINE_ATTRIB_KEY: responseData[j].LINE_ATTRIB_KEY,
                  ATTRIB_NAME: responseData[j].ATTRIB_NAME,
                  FORM_KEY: responseData[j].FORM_KEY,
                  OVERRIDE_Y_N: responseData[j].OVERRIDE_Y_N ?? null,
                  TRANS_DETAILS_KEY: responseData[j].TRANS_DETAILS_KEY ?? null,
                  TRANS_HEADER_KEY: responseData[j].TRANS_HEADER_KEY ?? null,
                  TRANS_TYPE_KEY: responseData[j].TRANS_TYPE_KEY ?? null
                };
                j++;
                k++;
              }
              i = j;
              this.GHOSTAmt.push(tempObj);
            }
            this.loadFlag = true;
            this.restoreGHOSTAmt();
          } else {
            for (const line of this.irsF5471ScheduleJData) {
              if (
                !_.find(this.col_total_line_arr, { line_no: line.line_no }) &&
                line.line_no?.split('-')[0] !== 'II'
              ) {
                this.computeRowTotal(line.line_no);
              }
            }
            this.getColTotal('II');
            this.loadFlag = false;
            this.irsF5471ScheduleJOriginalData = _.cloneDeep(this.irsF5471ScheduleJData);
          }
          this.cdr.markForCheck();
        } else {
          if (data.errorMessage === 'no access') {
            this.gtwSnackbarService.openSnackBar('Sorry you do not have access to do the requested action.', 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar('An error has occurred, If you continue to experience this issue please visit the help page to contact support.', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        }
      });
  }

  restoreGHOSTAmt(): void {
    const ghost_amt_Change_line_arr: string[] = [];
    if (this.loadFlag) {
      for (const ghostItem of this.GHOSTAmt) {
        const irs_line_obj = _.find(this.irsF5471ScheduleJData, { line_no: ghostItem.line_no });
        ghost_amt_Change_line_arr.push(ghostItem.line_no);
        if (irs_line_obj) {
          for (const tempGHOSTObj of ghostItem.col_data) {
            const irs_line_attrib_obj = _.find(irs_line_obj.column_description, {
              attrib_name: tempGHOSTObj.ATTRIB_NAME
            });
            if (irs_line_attrib_obj?.is_editable && tempGHOSTObj.OVERRIDE_Y_N === 'N') {
              irs_line_attrib_obj.VALUE = tempGHOSTObj.ADJ_AMT;
            }
          }
        }
      }
    } else {
      this.saveFlag = false;
      for (const ghostItem of this.GHOSTAmt) {
        const irs_line_obj = _.find(this.irsF5471ScheduleJData, { line_no: ghostItem.line_no });
        ghost_amt_Change_line_arr.push(ghostItem.line_no);
        if (irs_line_obj) {
          for (const tempGHOSTObj of ghostItem.col_data) {
            const irs_line_attrib_obj = _.find(irs_line_obj.column_description, {
              attrib_name: tempGHOSTObj.ATTRIB_NAME
            });
            if (irs_line_attrib_obj?.is_editable) {
              irs_line_attrib_obj.VALUE = tempGHOSTObj.ADJ_AMT;
            }
          }
        }
      }
    }

    for (const lineNo of ghost_amt_Change_line_arr) {
      if (lineNo?.split('-')[0] !== 'II') {
        this.computeRowTotal(lineNo);
      } else {
        this.getColTotal(lineNo.split('-')[0]);
      }
    }

    if (this.loadFlag && ghost_amt_Change_line_arr.length > 0) {
      this.irsF5471ScheduleJOriginalData = _.cloneDeep(this.irsF5471ScheduleJData);
      this.loadFlag = false;
    }
  }

  splitLineNo(lineNo: string, delimiter: string, index: number): string {
    return lineNo ? (lineNo.split(delimiter)[index] || '') : '';
  }

  computeRowTotal(v_line_no: string): void {
    const line_obj = _.find(this.irsF5471ScheduleJData, { line_no: v_line_no });
    if (!line_obj?.column_description) return;

    let total: string | null = null;
    const len = line_obj.column_description.length;
    let i = 0;

    while (i < len - 1) {
      const col = line_obj.column_description[i];
      if (col?.is_Display && col?.VALUE != null) {
        total = total != null ? (parseInt(total, 10) + parseInt(col.VALUE as any, 10)).toString() : String(col.VALUE);
      }
      i++;
    }

    const lastCol = line_obj.column_description[len - 1];
    if (lastCol?.is_Display) {
      lastCol.VALUE = total != null ? parseInt(total, 10) : null;
    }
    this.computeColTotal();
  }

  computeColTotal(): void {
    for (const colTotalItem of this.col_total_line_arr) {
      const total_line_obj = _.find(this.irsF5471ScheduleJData, { line_no: colTotalItem.line_no });
      const total_line_obj1 = colTotalItem.copy_total_line_no
        ? _.find(this.irsF5471ScheduleJData, { line_no: colTotalItem.copy_total_line_no })
        : null;

      if (!total_line_obj?.column_description) continue;

      for (let j = 0; j < total_line_obj.column_description.length; j++) {
        const colDesc = total_line_obj.column_description[j];
        if (!colDesc?.is_Display) continue;

        let total: string | null = null;
        for (const lineItem of colTotalItem.line_no_for_total) {
          const line_obj = _.find(this.irsF5471ScheduleJData, { line_no: lineItem.line_no });
          const line_attrib_obj = line_obj ? _.find(line_obj.column_description, { attrib_name: colDesc.attrib_name }) : null;
          if (line_attrib_obj?.VALUE != null) {
            const val = parseInt(String(line_attrib_obj.VALUE), 10) * lineItem.multiplier;
            total = total != null ? (parseInt(total, 10) + val).toString() : String(val);
          }
        }
        colDesc.VALUE = total != null ? parseInt(total, 10) : null;
        if (total_line_obj1?.column_description?.[j]) {
          total_line_obj1.column_description[j].VALUE = total != null ? parseInt(total, 10) : null;
        }
      }
      this.getRowTotal(colTotalItem.line_no);
      if (colTotalItem.copy_total_line_no) {
        this.getRowTotal(colTotalItem.copy_total_line_no);
      }
    }
  }

  getRowTotal(v_line_no: string): void {
    const line_obj = _.find(this.irsF5471ScheduleJData, { line_no: v_line_no });
    if (!line_obj?.column_description) return;

    let total: string | null = null;
    const len = line_obj.column_description.length;
    let i = 0;

    while (i < len - 1) {
      const col = line_obj.column_description[i];
      if (col?.is_Display && col?.VALUE != null) {
        total = total != null ? (parseInt(total, 10) + parseInt(col.VALUE as any, 10)).toString() : String(col.VALUE);
      }
      i++;
    }

    const lastCol = line_obj.column_description[len - 1];
    if (lastCol?.is_Display) {
      lastCol.VALUE = total != null ? parseInt(total, 10) : null;
    }
  }

  getColTotal(part_no: string): void {
    let total: string | null = null;
    for (const line of this.irsF5471ScheduleJData) {
      if (line.part_no === part_no) {
        if (line.line_no !== 'II-4') {
          const line_attrib_obj = line.column_description?.[0];
          if (line_attrib_obj?.VALUE != null) {
            total = total != null ? (parseInt(total, 10) + parseInt(String(line_attrib_obj.VALUE), 10)).toString() : String(line_attrib_obj.VALUE);
          }
        } else if (line.line_no === 'II-4' && line.column_description?.[0]) {
          line.column_description[0].VALUE = total != null ? parseInt(total, 10) : null;
        }
      }
    }
  }

  onFieldChange(lineNo: string, partNo?: string): void {
    if (partNo !== 'II') {
      this.computeRowTotal(lineNo);
    } else {
      this.getColTotal(partNo);
    }
  }

  getPartILines(): ScheduleJLineItem[] {
    if (!this.partFlag) return this.irsF5471ScheduleJData;
    return this.irsF5471ScheduleJData.filter(line => line.part_no === 'I');
  }

  getPartIILines(): ScheduleJLineItem[] {
    return this.irsF5471ScheduleJData.filter(line => line.part_no === 'II');
  }

  trackByLineNo(index: number, item: ScheduleJLineItem): string {
    return item.line_no;
  }

  trackByIndex(index: number): number {
    return index;
  }

  saveScheduleJData(): void {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    this.crudLoading = true;
    const updateOverrideFlag: any[] = [];
    const editRow: any[] = [];

    const scheduleSettings = [
      {
        tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
        scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
        jcd_key: this.globalService?.globalParams?.jcd_key ?? 250
      }
    ];

    for (const line of this.irsF5471ScheduleJData) {
      const ghost_line_obj = _.find(this.GHOSTAmt, { line_no: line.line_no });
      for (const col of line.column_description) {
        if (col.is_Display && col.VALUE != null) {
          editRow.push([
            {
              tax_year: this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year,
              trans_type_key: line.saveParams.trans_type_key,
              combination_key: this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY,
              group_obj_key: this.rowData?.GROUP_OBJ_KEY,
              form_key: line.saveParams.form_key,
              line_no: line.line_no,
              occurence: col.occurrence === '0' ? null : col.occurrence,
              line_attrib_key: col.line_attrib_key,
              Attribute_Name: col.attrib_name,
              Attribute_Value: col.VALUE != null ? col.VALUE : 0,
              trans_dtls_key: col.trans_details_key === '0' ? null : col.trans_details_key,
              trans_status: 'A'
            }
          ]);

          if (ghost_line_obj && col.is_editable) {
            const ghost_attrib_obj = _.find(ghost_line_obj.col_data, { ATTRIB_NAME: col.attrib_name });
            if (
              ghost_attrib_obj &&
              ((col.VALUE !== ghost_attrib_obj.ADJ_AMT &&
                ghost_attrib_obj.OVERRIDE_Y_N !== 'Y' &&
                ghost_attrib_obj.TRANS_DETAILS_KEY != null) ||
                (col.VALUE === ghost_attrib_obj.ADJ_AMT &&
                  ghost_attrib_obj.OVERRIDE_Y_N !== 'N' &&
                  ghost_attrib_obj.TRANS_DETAILS_KEY != null))
            ) {
              updateOverrideFlag.push([
                {
                  trans_header_key: ghost_attrib_obj.TRANS_HEADER_KEY,
                  trans_details_key: ghost_attrib_obj.TRANS_DETAILS_KEY,
                  trans_type_key: ghost_attrib_obj.TRANS_TYPE_KEY,
                  line_attrib_key: ghost_attrib_obj.LINE_ATTRIB_KEY,
                  form_key: ghost_attrib_obj.FORM_KEY,
                  OVERRIDE_Y_N: ghost_attrib_obj.OVERRIDE_Y_N === 'Y' ? 'N' : 'Y'
                }
              ]);
            }
          }
        }
      }
    }

    const scheduleDetails = { addrows: [], editrows: editRow, removerows: [] };
    const message = 'Schedule J details are successfully saved/edited';
    const baseUrl = this.baseURLs?.api || this.baseURLs?.base_url || this.GENERAL_CONFIG?.base_url;

    this.fir5471ScheduleJService
      .saveScheduleJ(
        baseUrl,
        scheduleSettings,
        scheduleDetails,
        this.globalService?.globalParams || {},
        this.filterParams
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (result: any) => {
          if (result.errorMessage && result.errorMessage !== 'null') {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
            this.crudLoading = false;
            this.cdr.markForCheck();
          } else {
            this.saveFlag = true;
            this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
            if (updateOverrideFlag.length > 0) {
              const jsonSettings = [
                {
                  combination_key: this.rowData?.HO_COMBINATION_KEY || this.rowData?.COMBINATION_KEY,
                  tax_year:
                    this.filterParams?.tax_year ||
                    this.globalService?.globalParams?.tax_year ||
                    this.rowData?.TAX_YEAR
                }
              ];
              this.fir5471ScheduleJService
                .updateFlag(baseUrl, jsonSettings, updateOverrideFlag, this.globalService?.globalParams || {}, this.filterParams)
                .pipe(takeUntil(this.unsubscribe$))
                .subscribe((flagResult: any) => {
                  this.crudLoading = false;
                });
            }
            this.crudLoading = false;
            this.loadFlag = false;
            this.reinitialize();
            this.cdr.markForCheck();
          }
        },
        () => {
          this.crudLoading = false;
          this.cdr.markForCheck();
        }
      );
  }

  reset(): void {
    this.crudLoading = false;
    this.saveFlag = true;
    this.irsF5471ScheduleJData = _.cloneDeep(this.irsF5471ScheduleJOriginalData);
    for (const line of this.irsF5471ScheduleJData) {
      if (
        !_.find(this.col_total_line_arr, { line_no: line.line_no }) &&
        line.line_no?.split('-')[0] !== 'II'
      ) {
        this.computeRowTotal(line.line_no);
      }
    }
    this.getColTotal('II');
    this.cdr.markForCheck();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  reinitialize(): void {
    this.isInitialized = false;
    this.tryInit();
  }
}
